/**
 * ehr#571 (PROGRESS_NOTE_10-20-V141, V120 / V123): a note signed more than the practice's late-entry
 * hours (72 by default) after the session asks for the reason before it goes. Time is measured from
 * the session's end (its start when it has no end), on this device's clock.
 */

/** "3:50 PM", "3:50pm" or "15:50" as minutes after midnight; null when it can't be read. */
export function minutesOf(t: string | undefined | null): number | null {
  const m = /^\s*(\d{1,2}):(\d{2})\s*([ap])?\.?\s*m?\.?\s*$/i.exec(t ?? '');
  if (!m) return null;
  let h = Number(m[1]);
  const min = Number(m[2]);
  const ap = m[3]?.toLowerCase();
  if (min > 59 || h > 23 || (ap && (h < 1 || h > 12))) return null;
  if (ap === 'p' && h < 12) h += 12;
  if (ap === 'a' && h === 12) h = 0;
  return h * 60 + min;
}

/** When the session ended (local time): its date and its end, else its start. Null without both. */
export function sessionEndAt(
  date: string | undefined | null,
  end: string | undefined | null,
  start?: string | null,
): Date | null {
  const d = /^(\d{4})-(\d{2})-(\d{2})/.exec(date ?? '');
  const mins = minutesOf(end) ?? minutesOf(start);
  if (!d || mins === null) return null;
  return new Date(Number(d[1]), Number(d[2]) - 1, Number(d[3]), Math.floor(mins / 60), mins % 60);
}

/** Whole hours from the session's end to now (0 before it ends). */
export function hoursAfterSession(end: Date, now: Date): number {
  return Math.max(0, Math.floor((now.getTime() - end.getTime()) / 3_600_000));
}

/** Late: more than the practice's hours after the session. */
export const isLateEntry = (hours: number, limit: number): boolean => hours > limit;

/** "9 days after the session", or "50 hours after the session" under two days. */
export function lateText(hours: number): string {
  if (hours >= 48) {
    const days = Math.floor(hours / 24);
    return `${days} days after the session`;
  }
  return `${hours} hour${hours === 1 ? '' : 's'} after the session`;
}

/** What a late note keeps (body.lateEntry): how many hours after the session, and why. */
export interface LateEntry {
  hours: number;
  reason: string;
}
/** The saved late entry, or null when there is none (or it isn't well formed). */
export function lateEntryOf(v: unknown): LateEntry | null {
  if (!v || typeof v !== 'object') return null;
  const o = v as Record<string, unknown>;
  return typeof o.hours === 'number' && typeof o.reason === 'string' && o.reason.trim()
    ? { hours: o.hours, reason: o.reason }
    : null;
}
